if (!customElements.get('product-bundle')) {
  class ProductBundle extends HTMLElement {

    /* ─────────────────────────────────────────────────────────────
       CONSTRUCTOR
    ───────────────────────────────────────────────────────────── */
    constructor() {
      super();

      /* State */
      this._pendingQty   = 0;       // optimistic quantity (updated on every click)
      this._serverQty    = 0;       // last confirmed quantity from Shopify cart
      this._atcInFlight    = false;   // true while /cart/add.js is running
      this._syncInFlight  = false;   // true while /cart/change.js (update) is running
      this._removeInFlight = false;  // true while /cart/change.js qty=0 (remove) is running
      this._debouncedSync = this._debounce(this._syncQuantity.bind(this), 400);

      /* DOM refs */
      this.productBundleBtn = this.querySelector('.product-bundle-atc-btn');
      this.bundleLineItem   = this.productBundleBtn?.dataset.bundleLineItem;

      if (this.productBundleBtn) {
        this.productBundleBtn.addEventListener('click', () => this._onAtcClick());
      }
    }

    /* ─────────────────────────────────────────────────────────────
       LIFECYCLE
    ───────────────────────────────────────────────────────────── */
    connectedCallback() {
      if (this.__connected) return;
      this.__connected = true;

      /* Listen for CartStore updates (covers page-load + other tabs) */
      this._onStoreUpdated = (e) => this._hydrateFromCart(e.detail.cart);
      document.documentElement.addEventListener('cart:store-updated', this._onStoreUpdated);

      /* Hydrate immediately from cached CartStore */
      const cached = window.CartStore?.get();
      if (cached) this._hydrateFromCart(cached);
    }

    disconnectedCallback() {
      document.documentElement.removeEventListener('cart:store-updated', this._onStoreUpdated);
      this.__connected = false;
    }

    /* ─────────────────────────────────────────────────────────────
       HYDRATE FROM CART (page-load / store update)
       Checks if the bundle's first item is already in cart.
    ───────────────────────────────────────────────────────────── */
    _hydrateFromCart(cart) {
      if (!cart || !Array.isArray(cart.items)) return;

      const firstId = Number(this.dataset.firstProductId);
      if (!firstId) return;

      const cartItem = cart.items.find(
        i => Number(i.variant_id) === firstId || Number(i.id) === firstId
      );

      if (cartItem && cartItem.quantity > 0) {
        /* Bundle is already in cart — show qty selector (skip if add/remove in-flight) */
        if (!this._atcInFlight && !this._removeInFlight) {
          this._serverQty  = cartItem.quantity;
          this._pendingQty = cartItem.quantity;
          this._showQtySelector(cartItem.quantity);
        }
      } else if (!this._atcInFlight && !this._removeInFlight) {
        /* Not in cart and no add/remove pending — show ATC */
        this._showAtcButton();
      }
    }

    /* ─────────────────────────────────────────────────────────────
       ATC CLICK
    ───────────────────────────────────────────────────────────── */
    _onAtcClick() {
      /* Block if add already in-flight, a remove is pending, or global cart is busy */
      if (this._atcInFlight || this._removeInFlight || window.CartBusy?.isBusy()) return;

      /* Optimistically switch UI to qty selector immediately */
      this._atcInFlight = true;
      this._pendingQty  = 1;
      this._showQtySelector(1, /* loading */ true);

      this._addBundleProductsToCart();
    }

    /* ─────────────────────────────────────────────────────────────
       PLUS / MINUS CLICKS
    ───────────────────────────────────────────────────────────── */
    _onPlusClick() {
      if (this._atcInFlight || this._syncInFlight || window.CartBusy?.isBusy()) return;

      const maxQty = parseInt(this.dataset.firstProductInventory || '999999', 10);
      const next   = this._pendingQty + 1;
      if (next > maxQty) { this._showMaxQuantityMessage(); return; }

      this._pendingQty = next;
      this._updateQtyDisplay(next);
      this._optimisticDelta(1);
      this._debouncedSync();
    }

    _onMinusClick() {
      if (this._atcInFlight || this._syncInFlight || this._removeInFlight || window.CartBusy?.isBusy()) return;

      const next = this._pendingQty - 1;

      if (next <= 0) {
        /* ── Decrement to zero → remove from cart ──

           CRITICAL ORDER: get the line key FIRST, then call optimisticDelta.
           CartStore.optimisticDelta filters out items whose quantity reaches 0,
           so _getLineKey() would return null if called after the delta. */
        const lineKey = this._getLineKey();

        /* Cancel any pending qty-update sync to avoid change.js racing remove */
        this._debouncedSync.cancel();

        this._pendingQty     = 0;
        this._removeInFlight = true;
        this._showAtcButton(/* disabled */ true);
        this._optimisticDelta(-this._serverQty);
        this._removeFromCart(lineKey);
      } else {
        this._pendingQty = next;
        this._updateQtyDisplay(next);
        this._optimisticDelta(-1);
        this._debouncedSync();
      }
    }

    /* ─────────────────────────────────────────────────────────────
       UI HELPERS
    ───────────────────────────────────────────────────────────── */
    _showAtcButton(disabled = false) {
      this.classList.remove('bundle-is-in-cart');
      const btn = this.querySelector('.product-bundle-atc-btn');
      if (btn) {
        btn.disabled         = disabled;
        btn.style.opacity    = disabled ? '0.6' : '';
        btn.style.cursor     = disabled ? 'not-allowed' : '';
      }
      /* Clear qty selector events so they don't fire stale */
      this._unbindQtyEvents();
    }

    _showQtySelector(qty, loading = false) {
      this.classList.add('bundle-is-in-cart');
      this._updateQtyDisplay(qty);
      this._setQtySelectorDisabled(loading);
      this._bindQtyEvents();
    }

    _updateQtyDisplay(qty) {
      const display = this.querySelector('.bundle-qty-selector__count');
      if (display) display.textContent = qty;
    }

    _setQtySelectorDisabled(disabled) {
      const minus = this.querySelector('.bundle-qty-selector__minus');
      const plus  = this.querySelector('.bundle-qty-selector__plus');
      [minus, plus].forEach(btn => {
        if (!btn) return;
        btn.disabled         = disabled;
        btn.style.opacity    = disabled ? '0.4' : '';
        btn.style.cursor     = disabled ? 'not-allowed' : '';
      });
    }

    _bindQtyEvents() {
      /* Prevent duplicate bindings */
      this._unbindQtyEvents();

      this._plusHandler  = () => this._onPlusClick();
      this._minusHandler = () => this._onMinusClick();

      const plus  = this.querySelector('.bundle-qty-selector__plus');
      const minus = this.querySelector('.bundle-qty-selector__minus');
      if (plus)  plus.addEventListener('click',  this._plusHandler);
      if (minus) minus.addEventListener('click', this._minusHandler);
    }

    _unbindQtyEvents() {
      if (this._plusHandler) {
        this.querySelector('.bundle-qty-selector__plus')?.removeEventListener('click',  this._plusHandler);
        this.querySelector('.bundle-qty-selector__minus')?.removeEventListener('click', this._minusHandler);
        this._plusHandler  = null;
        this._minusHandler = null;
      }
    }

    _showMaxQuantityMessage() {
      const popup = document.querySelector('.max-quantity-popup');
      if (!popup) return;
      popup.classList.remove('hidden');
      setTimeout(() => popup.classList.add('hidden'), 3000);
    }

    /* ─────────────────────────────────────────────────────────────
       CART STORE HELPERS
    ───────────────────────────────────────────────────────────── */
    _optimisticDelta(delta) {
      if (!window.CartStore) return;
      const id = this.dataset.firstProductId;
      window.CartStore.optimisticDelta(id, delta);
    }

    _getLineKey() {
      const cart = window.CartStore?.get();
      if (!cart || !Array.isArray(cart.items)) return null;
      const firstId = Number(this.dataset.firstProductId);
      const item    = cart.items.find(i => Number(i.variant_id) === firstId);
      return item?.key || null;
    }

    /* ─────────────────────────────────────────────────────────────
       /cart/add.js  — INITIAL ADD
    ───────────────────────────────────────────────────────────── */
    async _addBundleProductsToCart() {
      const firstProductID   = this.dataset.firstProductId;
      const secondProductID  = this.dataset.secondProductId;
      const thirdProductID   = this.dataset.thirdProductId;
      const fourthProductID  = this.dataset.fourthProductId;
      const fifthProductID   = this.dataset.fifthProductId;

      const items = [];
      const lineItemProperties = this.bundleLineItem !== undefined ? { Bundle: this.bundleLineItem } : {};

      if (firstProductID  !== undefined) items.push({ id: firstProductID,  quantity: 1, properties: lineItemProperties });
      if (secondProductID !== undefined) items.push({ id: secondProductID, quantity: 1, properties: lineItemProperties });
      if (thirdProductID  !== undefined) items.push({ id: thirdProductID,  quantity: 1, properties: lineItemProperties });
      if (fourthProductID !== undefined) items.push({ id: fourthProductID, quantity: 1, properties: lineItemProperties });
      if (fifthProductID  !== undefined) items.push({ id: fifthProductID,  quantity: 1, properties: lineItemProperties });

      const productHandles = [];
      if (this.dataset.firstProductHandle  !== undefined) productHandles.push(this.dataset.firstProductHandle);
      if (this.dataset.secondProductHandle !== undefined) productHandles.push(this.dataset.secondProductHandle);
      if (this.dataset.thirdProductHandle  !== undefined) productHandles.push(this.dataset.thirdProductHandle);
      if (this.dataset.fourthProductHandle !== undefined) productHandles.push(this.dataset.fourthProductHandle);
      if (this.dataset.fifthProductHandle  !== undefined) productHandles.push(this.dataset.fifthProductHandle);

      const bundleData = { items };
      const sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));
      bundleData['sections']     = sectionsToBundle.join(',');
      bundleData['sections_url'] = `${Shopify.routes.root}`;

      /* Shared product data */
      const priceInCents        = Number(this.dataset.productFloatingCartPrice) || 0;
      const comparePriceInCents = this._parsePriceToCents(this.dataset.productComparePrice);
      const productImage        = this.dataset.productImage || null;
      const productTitle        = this.dataset.productName  || '';
      const variantTitle        = this.dataset.productVariantTitle || null;

      /* Toast + optimistic CartStore */
      this._showToast();
      if (window.CartStore) {
        items.forEach(item => {
          window.CartStore.optimisticAdd({
            variantId:     Number(item.id),
            quantity:      item.quantity,
            price:         priceInCents,
            comparePrice:  comparePriceInCents,
            productTitle,
            variantTitle,
            productImage,
            productHandle: this.dataset.firstProductHandle || '',
            url:           `/products/${this.dataset.firstProductHandle || ''}`
          });
        });
      }

      items.forEach((item, index) => {
        document.documentElement.dispatchEvent(new CustomEvent('cart:add-initiated', {
          bubbles: true,
          detail: {
            variantId:     Number(item.id),
            quantity:      item.quantity,
            productTitle,
            variantTitle,
            productImage,
            price:         priceInCents,
            comparePrice:  comparePriceInCents,
            productHandle: productHandles[index] || productHandles[0] || '',
            url:           `/products/${productHandles[index] || productHandles[0] || ''}`
          }
        }));
      });

      try {
        const response     = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body:    JSON.stringify(bundleData)
        });
        const responseJson = await response.json();

        if (response.ok) {
          if (
            window.themeVariables?.settings.cartType === 'page' ||
            window.themeVariables?.settings.pageType === 'cart'
          ) {
            window.location.href = `${Shopify.routes.root}cart`;
            return;
          }

          /* Fetch real cart → update CartStore */
          const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          if (window.CartStore) window.CartStore.set(cartContent);
          cartContent.sections = responseJson.sections;

          /* Confirm server qty */
          const firstId   = Number(firstProductID);
          const cartItem  = cartContent.items?.find(i => Number(i.variant_id) === firstId);
          this._serverQty  = cartItem?.quantity || 1;
          this._pendingQty = this._serverQty;

          /* Enable qty selector */
          this._setQtySelectorDisabled(false);

          /* DataLayer tracking */
          this._pushTrackingEvents(cartContent, responseJson);

          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: { items: responseJson.items ?? [responseJson], cart: cartContent }
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', cart: cartContent }
          }));
          productHandles.forEach(each => {
            document.querySelector("[is='product-card-form']")?.updateAddtoCart(each);
          });

        } else {
          /* ── API failure: rollback to ATC ── */
          this._atcInFlight = false;
          this._pendingQty  = 0;
          this._serverQty   = 0;
          this._showAtcButton(false);
          this.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: responseJson['description'] } }));
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          const maxQuantityPopup = document.querySelector('.max-quantity-popup');
          if (maxQuantityPopup) {
            maxQuantityPopup.classList.remove('hidden');
            setTimeout(() => maxQuantityPopup.classList.add('hidden'), 3000);
          }
        }
      } catch (err) {
        /* Network failure: rollback */
        console.error('[ProductBundle] /cart/add.js failed:', err);
        this._atcInFlight = false;
        this._pendingQty  = 0;
        this._serverQty   = 0;
        this._showAtcButton(false);
      } finally {
        this._atcInFlight = false;
      }
    }

    /* ─────────────────────────────────────────────────────────────
       /cart/change.js — INCREMENT / DECREMENT
    ───────────────────────────────────────────────────────────── */
    async _syncQuantity() {
      // Guard: do not run if another sync or a remove is already in-flight
      if (this._syncInFlight || this._removeInFlight) return;

      const lineKey  = this._getLineKey();
      if (!lineKey) return;

      const quantity = this._pendingQty;

      this._syncInFlight = true;
      window.CartBusy?.lock();
      this._setQtySelectorDisabled(true);

      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

        const res  = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: lineKey, quantity, sections: sectionsToBundle })
        });
        const cart = await res.json();

        this._serverQty  = quantity;
        this._pendingQty = quantity;

        if (window.CartStore) window.CartStore.set(cart);

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart }
        }));

      } catch (err) {
        /* Rollback pending qty to last confirmed server qty */
        console.error('[ProductBundle] /cart/change.js failed:', err);
        this._pendingQty = this._serverQty;
        this._updateQtyDisplay(this._serverQty);
      } finally {
        window.CartBusy?.unlock();
        this._syncInFlight = false;
        this._setQtySelectorDisabled(false);
      }
    }

    /* ─────────────────────────────────────────────────────────────
       /cart/change.js  quantity=0 — REMOVE
       lineKey is passed in by the caller (captured before optimisticDelta
       because CartStore removes the item from the array once qty hits 0).
    ───────────────────────────────────────────────────────────── */
    async _removeFromCart(lineKey) {
      if (!lineKey) {
        /* Item was never in cart (or CartStore is empty) — just re-enable ATC */
        this._removeInFlight = false;
        this._showAtcButton(false);
        return;
      }
      window.CartBusy?.lock();

      try {
        const res  = await fetch(`${Shopify.routes.root}cart/change.js`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ id: lineKey, quantity: 0 })
        });
        const cart = await res.json();

        this._serverQty  = 0;
        this._pendingQty = 0;

        if (window.CartStore) window.CartStore.set(cart);

        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart }
        }));

        /* ✅ Remove confirmed — re-enable ATC button */
        this._removeInFlight = false;
        this._showAtcButton(false);

      } catch (err) {
        /* Rollback: restore qty selector at serverQty */
        console.error('[ProductBundle] remove failed:', err);
        this._pendingQty     = this._serverQty;
        this._removeInFlight = false;
        this._showQtySelector(this._serverQty, false);
      } finally {
        window.CartBusy?.unlock();
      }
    }

    /* ─────────────────────────────────────────────────────────────
       TOAST
    ───────────────────────────────────────────────────────────── */
    _showToast(message = 'Product added to the cart!', duration = 1500) {
      const toast = document.getElementById('atc-toast');
      if (!toast) return;
      const textEl = toast.querySelector('.atc-toast__text');
      if (textEl) textEl.textContent = message;
      toast.classList.add('show');
      toast.setAttribute('aria-hidden', 'false');
      clearTimeout(toast._hideTimer);
      toast._hideTimer = setTimeout(() => {
        toast.classList.remove('show');
        toast.setAttribute('aria-hidden', 'true');
      }, duration);
    }

    /* ─────────────────────────────────────────────────────────────
       PRICE HELPER
    ───────────────────────────────────────────────────────────── */
    _parsePriceToCents(str) {
      if (!str) return 0;
      const val = parseFloat(String(str).replace(/[₹$€£,\s]/g, ''));
      return isNaN(val) ? 0 : Math.round(val * 100);
    }

    /* ─────────────────────────────────────────────────────────────
       DEBOUNCE UTILITY
    ───────────────────────────────────────────────────────────── */
    _debounce(fn, delay) {
      let timer;
      function debounced(...args) {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), delay);
      }
      /* cancel() kills the pending timer so the fn never fires */
      debounced.cancel = () => clearTimeout(timer);
      return debounced;
    }

    /* ─────────────────────────────────────────────────────────────
       TRACKING
    ───────────────────────────────────────────────────────────── */
    _pushTrackingEvents(cartContent, responseJson) {
      const trackingAddToCart = {
        event:              'add_to_cart_custom',
        product_id:         this.dataset.productId,
        product_name:       this.dataset.productName,
        product_currency:   this.dataset.productCurrency,
        product_variant:    this.dataset.productVariantTitle,
        variant_product_id: this.dataset.productVariantId,
        product_in_stock:   this.dataset.productInStock,
        product_rating:     this.dataset.productRating,
        product_badge:      this.dataset.productBadges,
        product_quantity:   '1',
        product_price:      this.dataset.productPrice,
        product_mrp:        this.dataset.productComparePrice,
        item_list_name:     this.dataset.itemListName,
        origin_location:    this.dataset.originLocation,
        section_name:       this.dataset.sectionName,
        cart_count:         `${cartContent.item_count}`,
        cart_value:         `₹${(cartContent.total_price / 100).toFixed(2)}`
      };
      const trackingBundle = {
        event:               'bundle_button',
        bundle_slide_number: this.dataset.buttonSlide,
        Bundle_id:           this.dataset.bundleProductHandle,
        Bundle_price:        this.dataset.bundleProductPrice,
        Bundle_currency:     this.dataset.bundleProductCurrency,
        Bundle_product_id:   this.dataset.bundleProductId,
        Bundle_product_name: this.dataset.bundleProductTitle
      };
      if (window.dataLayer) {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            dataLayer.push(trackingAddToCart);
            dataLayer.push(trackingBundle);
          }, { timeout: 1500 });
        } else {
          setTimeout(() => {
            dataLayer.push(trackingAddToCart);
            dataLayer.push(trackingBundle);
          }, 0);
        }
      }
    }
  }
  customElements.define('product-bundle', ProductBundle);
}

if (!customElements.get('bundle-popup-model')) {
  class BundlePopupOpener extends HTMLElement {
    constructor() {
      super()
      this.popupOpener = this.querySelectorAll(".custom-product-bundle__dot-btn");

      this.productVariantId = this.dataset.productVariantId
      this.loader = document.querySelector(".custom-loader-wrapper")
      this.popupModel = document.querySelector(".custom-product-bundle__bundle-popup-model");
      this.popupCloseBtn = document.querySelector(".bundle-popup-close-btn")
      this.popupCloseBtn.addEventListener("click", () => {
        this.closePopupModel()
      })

      this.popupOpener.forEach(each => {
        each.addEventListener('click', () => {
          this.productUrl = each.dataset.productId;
          this.openPopupModel(each);

          const bundleDotDatalayer = {
            event: 'bundle_image',
            bundle_slide: each.dataset.bundleSlidePosition,
            Bundle_id: each.dataset.bundleMainProductId,
            Bundle_price: each.dataset.bundleMainProductPrice,
            Bundle_currency: each.dataset.bundleProductCurrency,
            Bundle_product_id: each.dataset.bundleLookbookProductId,
            Bundle_product_name: each.dataset.bundleLookbookProductTitle,
          };

          if ('requestIdleCallback' in window) {
            requestIdleCallback(() => {
              dataLayer.push(bundleDotDatalayer);
            }, { timeout: 1000 });
          } else {
            setTimeout(() => {
              dataLayer.push(bundleDotDatalayer);
            }, 0);
          }
        });
      });


    }

    openPopupModel(eachEl) {
      this.loader.classList.add("active")
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;

      fetch(this.productUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, "text/html");
          const mainContentDestinationEl = document.querySelector(".custom-product-bundle__popup-content-wrapper");
          const mainContentSourceEl = html.querySelector("#main-product-details");
          this.loader.classList.remove("active")
          this.popupModel.classList.add("active");
          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (mainContentDestinationEl && mainContentSourceEl) {
            mainContentDestinationEl.innerHTML = mainContentSourceEl.innerHTML
            const addToCartBtnWrapper = mainContentSourceEl.querySelector(".product-info__buy-buttons")
            const bundlePopupBuyButtons = document.querySelector(".custom-product-bundle__popup-content-wrapper .product-info__buy-buttons")
            const bundleAtcBtnWrapper = this.querySelectorAll(".product-bundle-atc-btn-wrapper")
            const pdpATCButton = mainContentSourceEl.querySelector("[is=custom-button]")
            const currentBlockIndex = eachEl.dataset.currentBlock;
            const isVariantsHide = eachEl.dataset.hideVariants;
            const enableSingleBundle = eachEl.dataset.enableSingleBundle;

            if (enableSingleBundle === "true") {
              bundleAtcBtnWrapper.forEach(each => {
                if (each.dataset.currentSlide == currentBlockIndex) {
                  bundlePopupBuyButtons.innerHTML = each.innerHTML
                  const variantPickerElement = mainContentDestinationEl.querySelector('[data-block-type="variant-picker"]');
                  variantPickerElement.style.display = "none"
                }
              })
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        })
    }

    closePopupModel() {
      this.popupModel.classList.remove("active")
      // document.body.style.overflow = "unset"
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  }
  customElements.define("bundle-popup-model", BundlePopupOpener);
}

if (!customElements.get('custom-product-bundle-section')) {
  class CustomProductBundleSection extends HTMLElement {
    constructor() {
      super();
      // Bind methods to the custom element context
      this.adjustHeight = this.adjustHeight.bind(this);
      this.debouncedAdjustHeight = this.debounce(this.adjustHeight, 100);
      this.resizeObserver = null;
    }

    connectedCallback() {
      // Call onImagesLoaded to ensure images are loaded before adjusting height
      this.onImagesLoaded(() => {
        this.adjustHeight();
        this.setupResizeObserver();
      });

      // Use debounced version for resize events to prevent excessive calls
      window.addEventListener('load', this.adjustHeight);
      window.addEventListener('resize', this.debouncedAdjustHeight);
      
      // Also listen for orientation changes on mobile devices
      window.addEventListener('orientationchange', () => {
        setTimeout(this.adjustHeight, 100); // Small delay for orientation change
      });
    }

    // Debounce function to limit how often adjustHeight is called
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    // Setup ResizeObserver for more reliable resize detection
    setupResizeObserver() {
      if (!window.ResizeObserver) return;

      this.resizeObserver = new ResizeObserver((entries) => {
        // Only adjust if window width is > 989
        if (window.innerWidth > 989) {
          this.adjustHeight();
        }
      });

      // Observe the main container
      this.resizeObserver.observe(this);
      
      // Also observe first child containers for more accurate detection
      const firstChildContainers = this.querySelectorAll('.custom-product-bundle__img-container');
      firstChildContainers.forEach(container => {
        this.resizeObserver.observe(container);
      });
    }

    adjustHeight() {
      // Reset heights first to get accurate measurements
      this.resetHeights();
      
      // Small delay to ensure DOM has updated after reset
      requestAnimationFrame(() => {
        if (window.innerWidth > 989) {
          // Select all parent containers for the bundle elements
          const bundleContainers = this.querySelectorAll('.custom-product-bundle');
          const AllListContainers = this.querySelectorAll(".custom-product-bundle__bundle-wrapper");

          // Loop through all the bundle containers (slides)
          bundleContainers.forEach((bundle) => {
            const firstChild = bundle.querySelector('.custom-product-bundle__img-container');
            const secondChild = bundle.querySelector('.custom-product-bundle__bundling-main-container');
            const listContainer = bundle.querySelector('.custom-product-bundle__bundle-wrapper');
            const actionButton = bundle.querySelector('.product-bundle-atc-btn-wrapper');

            if (firstChild && secondChild && listContainer && actionButton) {
              // Force reflow to get accurate height
              firstChild.offsetHeight;
              
              // Set second child container height equal to the first child container's height
              const firstChildHeight = firstChild.getBoundingClientRect().height;
              secondChild.style.height = `${firstChildHeight}px`;

              // Calculate remaining height for listContainer
              const actionButtonHeight = actionButton.getBoundingClientRect().height;
              const remainingHeight = firstChildHeight - actionButtonHeight - 16; // 16px gap
              
              if (remainingHeight > 0) {
                listContainer.style.height = `${remainingHeight}px`;
              }
            }
          });
        } else {
          // Reset heights for mobile/smaller screens
          this.resetHeights();
        }
      });
    }

    // Reset all dynamic heights
    resetHeights() {
      const secondChildren = this.querySelectorAll('.custom-product-bundle__bundling-main-container');
      const listContainers = this.querySelectorAll('.custom-product-bundle__bundle-wrapper');
      
      secondChildren.forEach(child => {
        child.style.height = '';
      });
      
      listContainers.forEach(container => {
        container.style.height = '';
      });
    }

    // Ensure all images within firstChild are loaded before calculating height
    onImagesLoaded(callback) {
      const bundleContainers = this.querySelectorAll('.custom-product-bundle');
      let bundlesProcessed = 0;

      if (bundleContainers.length === 0) {
        callback();
        return;
      }

      bundleContainers.forEach((bundle) => {
        const firstChild = bundle.querySelector('.custom-product-bundle__img-container');
        if (!firstChild) {
          bundlesProcessed++;
          if (bundlesProcessed === bundleContainers.length) {
            callback();
          }
          return;
        }

        const images = firstChild.querySelectorAll('img');
        let imagesLoaded = 0;
        const totalImages = images.length;

        const checkComplete = () => {
          bundlesProcessed++;
          if (bundlesProcessed === bundleContainers.length) {
            callback();
          }
        };

        if (totalImages === 0) {
          checkComplete();
          return;
        }

        images.forEach((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            imagesLoaded++;
            if (imagesLoaded === totalImages) {
              checkComplete();
            }
          } else {
            const handleLoad = () => {
              imagesLoaded++;
              if (imagesLoaded === totalImages) {
                checkComplete();
              }
            };

            img.addEventListener('load', handleLoad, { once: true });
            img.addEventListener('error', handleLoad, { once: true }); // Handle broken images
          }
        });
      });
    }

    // Cleanup event listeners when the element is removed
    disconnectedCallback() {
      window.removeEventListener('load', this.adjustHeight);
      window.removeEventListener('resize', this.debouncedAdjustHeight);
      window.removeEventListener('orientationchange', this.adjustHeight);
      
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    }
  }
  customElements.define('custom-product-bundle-section', CustomProductBundleSection);
}
